import {
  FOLLOW_UP_OWNER_ROLE_LABEL,
  type FollowUpOwnerRole,
  type WorkQueueLane,
} from "@care-by-ella/contracts";
import {
  followUpLaneOptions,
  type FollowUpLaterValue,
  type FollowUpOwnerChoice,
} from "@/lib/follow-up-later";
import { LANE_LABEL } from "@/lib/work-queue-lanes";

// TES-1400 (Ö-426) — the four things "Följ upp senare" always asks:
// "ansvarig person eller roll, datum och tid för återkomst, vilken arbetskö
// ärendet återkommer i, synlig orsak". The labels are hers; the hints are
// ours (listed in the PR).
const FIELD =
  "rounded-md border border-surfaceMuted bg-surface p-sm text-sm disabled:opacity-60";

export function FollowUpLaterFields({
  idPrefix,
  value,
  onChange,
  myRole,
  problem,
  disabled,
}: {
  idPrefix: string;
  value: FollowUpLaterValue;
  onChange: (next: FollowUpLaterValue) => void;
  myRole: FollowUpOwnerRole;
  problem: string | null;
  disabled?: boolean;
}) {
  const lanes = followUpLaneOptions(value.owner, myRole);
  const setOwner = (owner: FollowUpOwnerChoice) =>
    // A lane the new owner does not have is dropped, never kept silently.
    onChange({
      ...value,
      owner,
      lane:
        value.lane && followUpLaneOptions(owner, myRole).includes(value.lane)
          ? value.lane
          : "",
    });

  return (
    <div className="flex flex-col gap-sm" data-testid="follow-up-later">
      <label
        className="flex flex-col gap-xs text-sm"
        htmlFor={`${idPrefix}-owner`}
      >
        <span className="text-xs font-semibold text-textSecondary">
          Ansvarig person eller roll
        </span>
        <select
          id={`${idPrefix}-owner`}
          value={value.owner}
          onChange={(e) => setOwner(e.target.value as FollowUpOwnerChoice)}
          disabled={disabled}
          className={FIELD}
        >
          <option value="me">Jag själv</option>
          <option value="coach">{FOLLOW_UP_OWNER_ROLE_LABEL.coach}</option>
          <option value="doctor">{FOLLOW_UP_OWNER_ROLE_LABEL.doctor}</option>
        </select>
      </label>
      <div className="flex flex-wrap gap-sm">
        <label
          className="flex flex-col gap-xs text-sm"
          htmlFor={`${idPrefix}-date`}
        >
          <span className="text-xs font-semibold text-textSecondary">
            Datum för återkomst
          </span>
          <input
            id={`${idPrefix}-date`}
            type="date"
            value={value.date}
            onChange={(e) => onChange({ ...value, date: e.target.value })}
            disabled={disabled}
            className={FIELD}
          />
        </label>
        <label
          className="flex flex-col gap-xs text-sm"
          htmlFor={`${idPrefix}-time`}
        >
          <span className="text-xs font-semibold text-textSecondary">
            Tid för återkomst
          </span>
          <input
            id={`${idPrefix}-time`}
            type="time"
            value={value.time}
            onChange={(e) => onChange({ ...value, time: e.target.value })}
            disabled={disabled}
            className={FIELD}
          />
        </label>
      </div>
      <label
        className="flex flex-col gap-xs text-sm"
        htmlFor={`${idPrefix}-lane`}
      >
        <span className="text-xs font-semibold text-textSecondary">
          Arbetskö ärendet återkommer i
        </span>
        <select
          id={`${idPrefix}-lane`}
          value={value.lane}
          onChange={(e) =>
            onChange({ ...value, lane: e.target.value as WorkQueueLane | "" })
          }
          disabled={disabled}
          className={FIELD}
        >
          <option value="">Välj arbetskö</option>
          {lanes.map((lane) => (
            <option key={lane} value={lane}>
              {LANE_LABEL[lane]}
            </option>
          ))}
        </select>
      </label>
      <label
        className="flex flex-col gap-xs text-sm"
        htmlFor={`${idPrefix}-reason`}
      >
        <span className="text-xs font-semibold text-textSecondary">
          Synlig orsak
        </span>
        <textarea
          id={`${idPrefix}-reason`}
          value={value.reason}
          onChange={(e) => onChange({ ...value, reason: e.target.value })}
          disabled={disabled}
          rows={2}
          maxLength={2000}
          placeholder="Visas på ärendet när det kommer tillbaka."
          className={FIELD}
        />
      </label>
      {problem ? (
        <p className="text-xs text-textSecondary" role="status">
          {problem}
        </p>
      ) : null}
    </div>
  );
}
